import {ReactInstance} from 'virtual-tour-viewer/src/modules/React360Proxy';
import CustomMousePanCameraController from '../CameraControllers/CustomMousePanCameraController';
import CustomScrollPanCameraController from '../CameraControllers/CustomScrollPanCameraController';
import KeyboardPanCameraController from '../CameraControllers/KeyboardPanCameraController';
import type {CameraController} from 'virtual-tour-viewer/src/modules/React360Proxy';
import EdapIntegrationProxy from '../modules/EdapIntegrationProxy';
import EdapInteractionTypes from '../modules/EdapInteractionTypes';
import FixedPointCameraController from '../CameraControllers/FixedPointCameraController';
import type {Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import PointsOfInterestCameraController from '../CameraControllers/PointsOfInterestCameraController';

export default class CameraDirector {
    _reactInstance: ReactInstance;
    _leadChangedCallbacks: Array;
    _lead: CameraController;
    _mouseCamera: CustomMousePanCameraController;
    _scrollCamera: CustomScrollPanCameraController;
    _keyboardCamera: KeyboardPanCameraController;
    _tourId: String;
    _floorId: String;
    _sphereId: String;
    _fixedPointCamera: FixedPointCameraController;
    _poiCamera: PointsOfInterestCameraController;
    _frame: HTMLElement;
    _canvas: HTMLElement;

    constructor(reactInstance, fov) {
        this._reactInstance = reactInstance;
        this._leadChangedCallbacks = [];
        this._frame = this._reactInstance._eventLayer;
        this._canvas = this._reactInstance.compositor._canvas;
        const fovRadians = (fov * Math.PI) / 180;
        const verticalBumper = Math.PI / 4;

        const desktopOptions = {
            desktopFriction: 0.95,
            desktopStopThreshold: 0.015,
            desktopVelocityMultiplier: 8,
        };

        const mobileOptions = {
            mobileFriction: 0.96,
            mobileStopThreshold: 0.01,
            mobileVelocityMultiplier: 12,
        };

        this._mouseCamera = new CustomMousePanCameraController({
            frame: this._frame,
            fov: fovRadians,
            verticalBumper,
            ...desktopOptions,
            ...mobileOptions,
        });

        this._scrollCamera = new CustomScrollPanCameraController({
            frame: this._frame,
            verticalBumper,
            fov: fovRadians,
        });

        this._keyboardCamera = new KeyboardPanCameraController({
            frame: this._frame,
            verticalBumper,
            fov: fovRadians,
        });

        this._fixedPointCamera = new FixedPointCameraController({
            verticalBumper,
            shouldHoldGaze: false,
        });

        this._poiCamera = new PointsOfInterestCameraController({
            verticalBumper,
            currentCameraQuaternionProvider: () => {
                return this._reactInstance.getCameraQuaternion();
            },
        });

        this._frame.addEventListener('keydown', (event) => this._onKeyDown(event));

        this._mouseCamera.subscribeHandledEvent(() => this._setLead(this._mouseCamera));
        this._mouseCamera.doOnceDblClick(() =>
            this._onInteractionCallback(EdapInteractionTypes.DOUBLE_CLICK)
        );
        this._mouseCamera.doOnceDrag(() =>
            this._onInteractionCallback(EdapInteractionTypes.MOUSE_PAN)
        );
        this._mouseCamera.doOnceDragTouch(() =>
            this._onInteractionCallback(EdapInteractionTypes.TOUCH_PAN)
        );
        this._mouseCamera.doOnceZoom((sender, eventArgs) => this._onZoomCallback(eventArgs));

        this._scrollCamera.subscribeHandledEvent(() => this._setLead(this._scrollCamera));
        this._scrollCamera.doOnceScroll(() =>
            this._onInteractionCallback(EdapInteractionTypes.SCROLL_PAN)
        );
        this._keyboardCamera.subscribeHandledEvent(() => this._setLead(this._keyboardCamera));
        this._poiCamera.subscribeHandledEvent(() => this._setLead(this._poiCamera));
        this._fixedPointCamera.subscribeHandledEvent(() => this._setLead(this._fixedPointCamera));

        this._reactInstance.controls.clearCameraControllers();

        this._reactInstance.controls.addCameraController(this._mouseCamera);
        this._reactInstance.controls.addCameraController(this._scrollCamera);
        this._reactInstance.controls.addCameraController(this._keyboardCamera);
        this._reactInstance.controls.addCameraController(this._fixedPointCamera);
        this._reactInstance.controls.addCameraController(this._poiCamera);

        this._tourId = '';
        this._floorId = '';
        this._sphereId = '';
    }

    setTourId(tourId) {
        this._tourId = tourId;
    }

    setFloorId(floorId) {
        this._floorId = floorId;
    }

    setSphereId(sphereId) {
        this._sphereId = sphereId;
    }

    _setLead(lead) {
        if (lead !== this._lead) {
            if (lead !== this._poiCamera) {
                this._poiCamera.disable();
            } else {
                this._poiCamera.enable();
            }
            this._lead = lead;
            this._leadChangedCallbacks.forEach((c) => c(lead));
        }
    }

    _onInteractionCallback(EdapInteractionType) {
        EdapIntegrationProxy.emitSphereInteraction(
            this._tourId,
            this._floorId,
            this._sphereId,
            EdapInteractionType
        );
    }

    _onZoomCallback(eventArgs) {
        let {scale} = eventArgs;
        if (scale < 1) {
            this._onInteractionCallback(EdapInteractionTypes.SPREAD);
            return;
        }
        if (scale > 1) {
            this._onInteractionCallback(EdapInteractionTypes.PINCH);
        }
    }

    _onKeyDown(eventArgs) {
        if (eventArgs.keyCode === 9) {
            // TAB
            let success;
            if (eventArgs.shiftKey) {
                success = this._poiCamera.previous();
            } else {
                success = this._poiCamera.next();
            }
            if (success) {
                eventArgs.preventDefault();
            }
        }
    }

    lookAt(point: Vec3) {
        this._fixedPointCamera.setPoint(point);
    }

    setPointsOfInterest(points: Array<Vec3>) {
        this._poiCamera.setPoints(points);
    }

    onLeadChanged(callback) {
        this._leadChangedCallbacks.push(callback);
    }

    offLeadChanged(callback) {
        this._leadChangedCallbacks = this._leadChangedCallbacks.filter((c) => c !== callback);
    }
}
